<template>
  <div class="animal-scan-list">
    <div class="list-header">
      <h3 class="list-title">Expected Animals</h3>
      <div class="scan-progress">
        <span class="progress-text">
          {{ expectedScannedCount }} / {{ expectedAnimals.length }} scanned
        </span>
      </div>
    </div>

    <div class="animal-list">
      <div
        v-for="animal in expectedAnimals"
        :key="animal.id"
        class="animal-item"
        :class="{
          'animal-scanned': isScanned(animal),
          'animal-pending': !isScanned(animal),
        }"
      >
        <div class="animal-status-icon">
          <i v-if="isScanned(animal)" class="pi pi-check-circle status-checked"></i>
          <i v-else class="pi pi-circle status-pending"></i>
        </div>
        <div class="animal-info">
          <div class="animal-header">
            <span class="animal-barcode">{{ getAnimalDisplayToken(animal) }}</span>
            <span v-if="animal.rfidTagId" class="rfid-badge">RFID: {{ animal.rfidTagId }}</span>
            <span v-if="animal.color" class="color-badge" :class="getColorClass(animal.color)">
              {{ animal.color }}
            </span>
          </div>
          <div class="animal-details">
            <div class="detail-row">
              <span class="detail-item">
                <span class="detail-label">Study:</span>
                <span class="detail-value">{{ animal.studyNumber || '—' }}</span>
              </span>
              <span class="detail-item">
                <span class="detail-label">Group:</span>
                <span class="detail-value">{{ animal.studyGroup || '—' }}</span>
              </span>
            </div>
            <div class="detail-row">
              <span class="detail-item">
                <span class="detail-label">Prime ID:</span>
                <span class="detail-value">{{ animal.primeAnimalId || '—' }}</span>
              </span>
              <span class="detail-item">
                <span class="detail-label">Home Cage:</span>
                <span class="detail-value">{{ animal.homeCageNumber || '—' }}</span>
              </span>
            </div>
            <div v-if="animal.location" class="detail-row">
              <span class="detail-item">
                <span class="detail-label">Location:</span>
                <span class="detail-value">{{ animal.location }}</span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="unexpectedAnimals.length > 0" class="unexpected-section">
      <h4 class="unexpected-title">Unexpected Animals Found</h4>
      <div class="animal-list">
        <div
          v-for="animal in unexpectedAnimals"
          :key="animal.rapidTagBarcode"
          class="animal-item animal-unexpected"
        >
          <div class="animal-status-icon">
            <i class="pi pi-exclamation-triangle status-unexpected"></i>
          </div>
          <div class="animal-info">
            <div class="animal-barcode">{{ animal.rapidTagBarcode }}</div>
            <div class="animal-timestamp">Scanned at: {{ formatTime(animal.scannedAt) }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { ExpectedAnimal, ScannedAnimal } from '../../stores/audit.store';

interface Props {
  expectedAnimals: ExpectedAnimal[];
  scannedAnimals: ScannedAnimal[];
}

const props = defineProps<Props>();

const expectedScannedCount = computed(() => {
  return props.scannedAnimals.filter((animal) => animal.isExpected).length;
});

const unexpectedAnimals = computed(() => {
  return props.scannedAnimals.filter((animal) => !animal.isExpected);
});

function isScanned(animal: ExpectedAnimal): boolean {
  return props.scannedAnimals.some(
    (scanned) => scanned.isExpected && scanned.animalId === animal.id,
  );
}

function getAnimalDisplayToken(animal: ExpectedAnimal): string {
  return animal.rapidTagBarcode || animal.rfidTagId || animal.id;
}

function formatTime(timestamp: string): string {
  const date = new Date(timestamp);
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function getColorClass(color: string): string {
  const colorLower = color.toLowerCase();
  const colorMap: Record<string, string> = {
    red: 'color-red',
    blue: 'color-blue',
    green: 'color-green',
    yellow: 'color-yellow',
    orange: 'color-orange',
    purple: 'color-purple',
    pink: 'color-pink',
    brown: 'color-brown',
    black: 'color-black',
    white: 'color-white',
  };
  return colorMap[colorLower] || 'color-default';
}
</script>

<style scoped>
.animal-scan-list {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  height: 100%;
  overflow: hidden;
}

.list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid #e5e7eb;
  flex-shrink: 0;
}

.list-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #111827;
  margin: 0;
}

.scan-progress {
  display: flex;
  align-items: center;
}

.progress-text {
  font-size: 1rem;
  font-weight: 600;
  color: #6b7280;
}

.animal-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
}

.animal-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  border-radius: 0.5rem;
  border: 2px solid;
  transition: all 0.2s ease;
}

.animal-item.animal-pending {
  background-color: #f9fafb;
  border-color: #e5e7eb;
}

.animal-item.animal-scanned {
  background-color: #d1fae5;
  border-color: #10b981;
}

.animal-item.animal-unexpected {
  background-color: #fef3c7;
  border-color: #f59e0b;
}

.animal-status-icon {
  flex-shrink: 0;
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
}

.status-checked {
  color: #10b981;
}

.status-pending {
  color: #9ca3af;
}

.status-unexpected {
  color: #f59e0b;
}

.animal-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.animal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.animal-barcode {
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
  font-family: monospace;
  letter-spacing: 0.1em;
}

.rfid-badge {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.25rem 0.5rem;
  border-radius: 9999px;
  background-color: #dbeafe;
  color: #1e40af;
  font-family: monospace;
  letter-spacing: 0.05em;
}

.color-badge {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.25rem 0.5rem;
  border-radius: 9999px;
  text-transform: capitalize;
}

.color-red {
  background-color: #fecaca;
  color: #991b1b;
}
.color-blue {
  background-color: #bfdbfe;
  color: #1e40af;
}
.color-green {
  background-color: #bbf7d0;
  color: #166534;
}
.color-yellow {
  background-color: #fef08a;
  color: #854d0e;
}
.color-orange {
  background-color: #fed7aa;
  color: #9a3412;
}
.color-purple {
  background-color: #e9d5ff;
  color: #6b21a8;
}
.color-pink {
  background-color: #fbcfe8;
  color: #9d174d;
}
.color-brown {
  background-color: #d6ccc2;
  color: #44403c;
}
.color-black {
  background-color: #374151;
  color: #f9fafb;
}
.color-white {
  background-color: #f3f4f6;
  color: #374151;
  border: 1px solid #d1d5db;
}
.color-default {
  background-color: #e5e7eb;
  color: #374151;
}

.animal-details {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  padding-top: 0.375rem;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}

.detail-row {
  display: flex;
  gap: 1.5rem;
  flex-wrap: wrap;
}

.detail-item {
  display: flex;
  gap: 0.375rem;
  font-size: 0.8125rem;
}

.detail-label {
  color: #6b7280;
  font-weight: 500;
}

.detail-value {
  color: #374151;
  font-weight: 500;
}

.animal-color {
  font-size: 0.875rem;
  color: #6b7280;
}

.animal-timestamp {
  font-size: 0.875rem;
  color: #6b7280;
}

.unexpected-section {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 2px solid #fbbf24;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.unexpected-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #92400e;
  margin: 0 0 0.75rem 0;
  flex-shrink: 0;
}

/* Tablet/iPad specific styles */
@media (min-width: 768px) and (max-width: 1024px) {
  .animal-scan-list {
    gap: 0.5rem;
  }

  .list-header {
    padding-bottom: 0.5rem;
    margin-bottom: 0.25rem;
  }

  .list-title {
    font-size: 1rem;
  }

  .scan-progress .progress-text {
    font-size: 0.875rem;
  }

  .animal-item {
    padding: 0.5rem 0.625rem;
    gap: 0.625rem;
  }

  .animal-status-icon {
    width: 1.25rem;
    height: 1.25rem;
    font-size: 0.9375rem;
  }

  .animal-barcode {
    font-size: 0.875rem;
  }

  .color-badge {
    font-size: 0.625rem;
    padding: 0.125rem 0.375rem;
  }

  .animal-details {
    gap: 0.25rem;
    padding-top: 0.25rem;
  }

  .detail-row {
    gap: 0.75rem;
  }

  .detail-item {
    font-size: 0.6875rem;
    gap: 0.25rem;
  }

  .animal-color {
    font-size: 0.75rem;
  }

  .animal-timestamp {
    font-size: 0.75rem;
  }

  .animal-scan-list {
    height: 100%;
    gap: 0.5rem;
  }

  .list-header {
    padding-bottom: 0.375rem;
    margin-bottom: 0.25rem;
  }

  .animal-list {
    flex: 1;
    min-height: 0;
    gap: 0.375rem;
    overflow-y: auto;
    overflow-x: hidden;
    -webkit-overflow-scrolling: touch;
  }

  .unexpected-section {
    margin-top: 0.5rem;
    padding-top: 0.5rem;
    flex: 1;
    min-height: 0;
    overflow: hidden;
  }

  .unexpected-title {
    font-size: 1rem;
    margin: 0 0 0.5rem 0;
    flex-shrink: 0;
  }

  .unexpected-section .animal-list {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    -webkit-overflow-scrolling: touch;
  }
}

@media (max-width: 768px) {
  .list-title {
    font-size: 1.25rem;
  }

  .animal-item {
    padding: 0.75rem;
  }

  .animal-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .animal-barcode {
    font-size: 1rem;
  }

  .detail-row {
    flex-direction: column;
    gap: 0.25rem;
  }

  .detail-item {
    font-size: 0.75rem;
  }

  .animal-list {
    max-height: 300px;
  }
}
</style>
